import {enableProdMode, ErrorHandler, inject, provideAppInitializer} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter, Router, RouteReuseStrategy, withRouterConfig} from '@angular/router';


import {routes} from './app/app.routes';
import {AppComponent, setServicesFactory, storageFactory} from './app/app.component';
import {environment} from './environments/environment';
import {OAuthStorage, provideOAuthClient} from 'angular-oauth2-oidc';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {AppConfigService} from "./app/config/app-config.service";
import {AuthService} from "./app/api/auth/auth.service";
import {HttpErrorInterceptor} from "./app/api/error/http-error.interceptor";
import {WriteTimeoutInterceptor} from "./app/api/timeout/write-timeout.interceptor";
import {DirtyDataInterceptor} from "./app/api/dirtyData/dirty-data.interceptor";
import {defineCustomElements} from '@ionic/pwa-elements/loader';

import * as Sentry from "@sentry/angular";
import {TraceService} from "@sentry/angular";
import {SpinnerInterceptor} from "./app/api/spinner/spinner.interceptor";
import {GlobalErrorHandler} from "./app/global-error-handler";
import {IonicRouteStrategy, provideIonicAngular} from "@ionic/angular/standalone";
import {register as registerSwiperElements} from 'swiper/element/bundle';
import {SubscriptionService} from "./app/api/subscription/subscription.service";
import {PURCHASE_PROVIDER} from "./app/api/subscription/purchase-provider.interface";
import {CapacitorPurchaseProvider} from "./app/api/subscription/capacitor-purchase-provider";
import {WebPurchaseProvider} from "./app/api/subscription/web-purchase-provider";
import {purchaseProviderFactory} from "./app/api/subscription/purchase-provider.factory";
import {SplashScreenService} from "./app/api/splash-screen/splash-screen.service";
import {LoggerService} from "./app/api/logging/logger.service";
import {OnlineOfflineService} from "./app/api/onlineOffline/online-offline.service";
import {PhotoService} from "./app/api/photo/photo.service";
import {PhotoRecoveryService} from "./app/api/photo/photo-recovery.service";
import {DirtyDataWriterService} from "./app/api/dirtyData/dirty-data-writer.service";
import {NotificationService} from "./app/api/notifications/notification.service";
import {TracePropagationInterceptor} from "./app/api/tracing/trace-propagation.interceptor";
import {PostHogService} from "./app/api/posthog/posthog.service";
import {ThemeService} from "./app/api/theme/theme.service";
import {EventDraftService} from "./app/api/event/event-draft.service";
import type {IonicConfig} from "@ionic/core/components";
import {version} from "../package.json";
import {
  buildSentryInitOptions,
  resolveSentryEnvironment,
  shouldEnableSentry
} from "./app/utils/sentry-environment";

registerSwiperElements();

async function main(): Promise<void> {
  // Angular production mode is a build concern — tie it to the compile-time flag.
  if (environment.production) {
    enableProdMode();
  }

  // Sentry's environment comes from runtime config (the same config
  // AppConfigService loads), so a single build artefact reports the correct
  // environment per deployment. Enabled for deployed environments only;
  // local dev stays off.
  const sentryEnv = await resolveSentryEnvironment('development');
  const sentryEnabled = shouldEnableSentry(sentryEnv);

  if (sentryEnabled) {
    Sentry.init(buildSentryInitOptions(sentryEnv, `hec@${version}`));
  }

  const ionicConfig: IonicConfig = {};

  bootstrapApplication(AppComponent, {
    providers: [
      {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
      provideIonicAngular(ionicConfig), // Remove _forceStatusbarPadding to enable dynamic safe area detection
      provideHttpClient(withInterceptorsFromDi()),
      provideOAuthClient({
        resourceServer: {
          allowedUrls: ['https://staging.hec-local.co.uk/hec/api',
                        'https://api.hec-dev.co.uk:8443/hec/api',
                        'https://api.homeedcompanion.com/hec/api',
                        'https://pre-prod.homeedcompanion.com/hec/api'],
          sendAccessToken: true
        },
      }),
      provideAppInitializer(() => setServicesFactory(
        inject(AppConfigService),
        inject(AuthService),
        inject(SplashScreenService),
        inject(SubscriptionService),
        inject(LoggerService),
        inject(PostHogService)
      )),
      // Non-blocking: registers the background flush of in-flight drafts
      // (HEC-178) so the last edit is on disk before the app backgrounds.
      provideAppInitializer(() => inject(EventDraftService).initialise()),
      provideAppInitializer(() => inject(OnlineOfflineService).initialize()),
      provideAppInitializer(() => inject(PhotoService).initialize()),
      // HEC-180: registers the restored camera-result listener. One bounded
      // bridge round trip on Android, a no-op on web and iOS.
      provideAppInitializer(() => inject(PhotoRecoveryService).initialise()),
      provideAppInitializer(() => inject(DirtyDataWriterService).initialize()),
      provideAppInitializer(() => inject(NotificationService).initialise()),
      // The colour scheme class must be on <html> before the first paint.
      provideAppInitializer(() => inject(ThemeService).initialise()),
      // Sentry tracing — only when Sentry is enabled
      ...(sentryEnabled ? [
        {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        provideAppInitializer(() => {
          inject(TraceService);
        }),
      ] : []),
      {provide: HTTP_INTERCEPTORS, useClass: TracePropagationInterceptor, multi: true},
      {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true},
      // Load-bearing position: between HttpErrorInterceptor and
      // DirtyDataInterceptor, so a stalled write is bounded but the
      // offline queue's isConnectionError check never sees it as a
      // connection error and silently swallows it (HEC-347).
      {provide: HTTP_INTERCEPTORS, useClass: WriteTimeoutInterceptor, multi: true},
      {provide: HTTP_INTERCEPTORS, useClass: DirtyDataInterceptor, multi: true},
      {provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptor, multi: true},
      {provide: OAuthStorage, useFactory: storageFactory},
      {provide: ErrorHandler, useClass: GlobalErrorHandler},
      CapacitorPurchaseProvider,
      WebPurchaseProvider,
      {
        provide: PURCHASE_PROVIDER,
        useFactory: purchaseProviderFactory,
        deps: [
          CapacitorPurchaseProvider,
          WebPurchaseProvider
        ]
      },
      // 'computed' puts the history index back where it was when a guard
      // cancels a browser or hardware back (the activity form's discard
      // prompt, HEC-178). The default 'replace' rewrote the popped entry
      // with the current URL, so the next back skipped the page the user
      // had just refused to leave for and never prompted again.
      provideRouter(routes, withRouterConfig({
        canceledNavigationResolution: 'computed'
      })),
    ],
  }).catch(err => {
    Sentry.captureException(err);
    console.error('Bootstrap failed:', err);
  });

  defineCustomElements(window).catch(err => console.error('PWA elements failed to load:', err));
}

void main();
